/**
 * The CRF Builder's side of the number flag (review item 16, stage 3,
 * Jim 2026-10-18).
 *
 * A question may carry `ungroundedNumbers` (numbers in it the protocol does
 * not state, computed on the server: functions/src/crf/numberFlags.ts) and
 * `numbersConfirmed` (the sponsor's confirmations, which every save keeps).
 * The page shows the active flag on the question with a Confirm action;
 * confirming adds the number(s) to `numbersConfirmed` and saves through the
 * normal save path, after which the number is no longer flagged anywhere.
 *
 * Pure, so functions/vitest.web.config.ts can test it.
 */

export interface NumberFlagLike {
  ungroundedNumbers?: unknown;
  numbersConfirmed?: unknown;
  numbersCheckedFor?: unknown;
}

const list = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string' && x.trim() !== '') : []);

/** A written number reduced for comparison, as the server does: "180 mmHg" = "180mmhg", "80 percent" = "80%". */
export function numberKey(written: string): string {
  return String(written ?? '')
    .replace(/(\d),(?=\d{3}(?!\d))/g, '$1')
    .toLowerCase()
    .replace(/\bper\s*cent\b|\bpercent\b/g, '%')
    .replace(/\s+/g, '');
}

const same = (a: string, b: string) => numberKey(a) === numberKey(b);

/** The numbers still flagged on a question: not stated in the protocol and not confirmed. */
export function activeNumberFlags(v: NumberFlagLike | null | undefined): string[] {
  const confirmed = list(v?.numbersConfirmed);
  return list(v?.ungroundedNumbers).filter((n) => !confirmed.some((c) => same(c, n)));
}

/** The badge text: "Number not in protocol: 80%". Empty for no active flag. */
export function numberFlagLabel(active: string[]): string {
  if (!active.length) return '';
  return `${active.length === 1 ? 'Number' : 'Numbers'} not in protocol: ${active.join(', ')}`;
}

/** The badge's tooltip. */
export function numberFlagTooltip(active: string[]): string {
  if (!active.length) return '';
  const what = active.join(', ');
  return `The protocol does not state ${what}. Confirm if ${active.length === 1 ? 'it is' : 'they are'} the study's own value, or edit the question. ` +
    'Until confirmed, the data dictionary and the completion guide carry a sponsor review note. The printed form is not changed.';
}

/** The question with its active numbers confirmed. The same object when nothing is active. */
export function confirmNumbers<V extends NumberFlagLike>(v: V): V {
  const active = activeNumberFlags(v);
  if (!active.length) return v;
  return { ...v, numbersConfirmed: [...list(v.numbersConfirmed), ...active] };
}

/** The form with one variable's active numbers confirmed. */
export function confirmVariableNumbers<F extends { variables?: Array<NumberFlagLike & { id: string }> | null }>(form: F, varId: string): F {
  if (!Array.isArray(form.variables)) return form;
  let changed = false;
  const variables = form.variables.map((v) => {
    if (v.id !== varId) return v;
    const next = confirmNumbers(v);
    if (next !== v) changed = true;
    return next;
  });
  return changed ? { ...form, variables } : form;
}

export interface SavedNumberFlag {
  varName: string;
  ungroundedNumbers?: string[];
  numbersConfirmed?: string[];
  numbersCheckedFor?: string;
}

const sameList = (a: unknown, b: unknown) => {
  const x = list(a);
  const y = list(b);
  return x.length === y.length && x.every((n, i) => n === y[i]);
};

/**
 * The form with the flags a save returned: each reported variable's three
 * fields replaced by the save's, matched by variable name. The same form when
 * nothing differs, so adopting causes no re-render and no further save.
 */
export function adoptSavedNumberFlags<F extends { variables?: Array<NumberFlagLike & { varName: string }> | null }>(
  form: F,
  flags: SavedNumberFlag[] | null | undefined,
): F {
  if (!Array.isArray(flags) || !flags.length || !Array.isArray(form.variables)) return form;
  const byVar = new Map(flags.map((f) => [f.varName, f]));
  let changed = false;
  const variables = form.variables.map((v) => {
    const f = byVar.get(v.varName);
    if (!f) return v;
    if (sameList(v.ungroundedNumbers, f.ungroundedNumbers) && sameList(v.numbersConfirmed, f.numbersConfirmed) && (v.numbersCheckedFor ?? undefined) === (f.numbersCheckedFor ?? undefined)) return v;
    changed = true;
    const { ungroundedNumbers: _u, numbersConfirmed: _c, numbersCheckedFor: _k, ...rest } = v;
    return {
      ...rest,
      ...(list(f.ungroundedNumbers).length ? { ungroundedNumbers: [...list(f.ungroundedNumbers)] } : {}),
      ...(list(f.numbersConfirmed).length ? { numbersConfirmed: [...list(f.numbersConfirmed)] } : {}),
      ...(typeof f.numbersCheckedFor === 'string' && f.numbersCheckedFor ? { numbersCheckedFor: f.numbersCheckedFor } : {}),
    } as typeof v;
  });
  return changed ? { ...form, variables } : form;
}
